/**
 * Servicio de Notificaciones Push - C&M Publicaciones
 * Maneja la suscripción y gestión de notificaciones push del navegador
 */

// URL del backend - usar URLs relativas en producción
const isLocalDev = typeof window !== 'undefined' && 
  (window.location.hostname === 'localhost' || 
   window.location.hostname.includes('preview.emergentagent.com'));
const API_URL = isLocalDev ? (process.env.REACT_APP_BACKEND_URL || '') : '';

/**
 * Verifica si el navegador soporta notificaciones push
 */
export const isPushSupported = () => {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

/**
 * Verifica el estado actual de los permisos de notificación
 */
export const getNotificationPermission = () => {
  if (!('Notification' in window)) {
    return 'unsupported';
  }
  return Notification.permission; // 'default', 'granted', 'denied'
};

/**
 * Solicita permiso para mostrar notificaciones
 */
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    throw new Error('Este navegador no soporta notificaciones');
  }
  
  const permission = await Notification.requestPermission();
  return permission;
};

/**
 * Registra el Service Worker para push
 */
export const registerServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) {
    throw new Error('Service Worker no soportado');
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/'
    });
    console.log('[Push] Service Worker registrado:', registration.scope);
    return registration;
  } catch (error) {
    console.error('[Push] Error registrando Service Worker:', error);
    throw error;
  }
};

/**
 * Obtiene la clave pública VAPID del servidor
 */
export const getVapidPublicKey = async () => {
  const response = await fetch(`${API_URL}/api/push/vapid-public-key`);
  if (!response.ok) {
    throw new Error('No se pudo obtener la clave VAPID');
  }
  const data = await response.json();
  return data.publicKey;
};

/**
 * Convierte una cadena base64 URL a Uint8Array (necesario para push)
 */
const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
};

/**
 * Suscribe al usuario a notificaciones push
 */
export const subscribeToPush = async (token) => {
  if (!isPushSupported()) {
    throw new Error('Push notifications no soportadas en este navegador');
  }
  
  // Solicitar permiso
  const permission = await requestNotificationPermission();
  if (permission !== 'granted') {
    throw new Error('Permiso de notificaciones denegado');
  }
  
  // Registrar service worker
  const registration = await registerServiceWorker();
  
  // Obtener clave VAPID
  const vapidPublicKey = await getVapidPublicKey();
  
  // Crear suscripción
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
  });
  
  // Enviar suscripción al servidor
  const response = await fetch(`${API_URL}/api/push/subscribe`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      endpoint: subscription.endpoint,
      keys: {
        p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
        auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
      }
    })
  });
  
  if (!response.ok) {
    throw new Error('Error al registrar suscripción en el servidor');
  }
  
  const data = await response.json();
  console.log('[Push] Suscripción registrada:', data);
  return data;
};

/**
 * Cancela la suscripción a notificaciones push
 */
export const unsubscribeFromPush = async (token) => {
  // Cancelar suscripción en el navegador
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    
    if (subscription) {
      await subscription.unsubscribe();
    }
  }
  
  // Cancelar en el servidor
  const response = await fetch(`${API_URL}/api/push/unsubscribe`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    throw new Error('Error al cancelar suscripción');
  }
  
  return await response.json();
};

/**
 * Verifica si el usuario está suscrito a push
 */
export const getPushStatus = async (token) => {
  const response = await fetch(`${API_URL}/api/push/status`, {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  });
  
  if (!response.ok) {
    return { configured: false, subscribed: false };
  }
  
  return await response.json();
};

/**
 * Envía una notificación de prueba
 */
export const sendTestNotification = async (token) => {
  const response = await fetch(`${API_URL}/api/push/test`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    },
    body: JSON.stringify({
      titulo: '🔔 Notificación de Prueba',
      mensaje: 'Las notificaciones push están funcionando correctamente. ¡Recibirás alertas de nuevos documentos!'
    })
  });
  
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Error al enviar notificación de prueba');
  }
  
  return await response.json();
};
